"use client";

import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import axios from "axios";
import jsPDF from "jspdf";
import aligarhLogo from "../../../assets/aligarh-logo.png";
import CloseIcon from "@mui/icons-material/Close";
import { useBranch } from "./../../layouts/BranchContext";

import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Modal,
  Paper,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TablePagination,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Tooltip,
  Divider,
  Alert,
  Autocomplete,
  IconButton,
  InputAdornment,
} from "@mui/material";

import { useTheme } from "@mui/material/styles";

import { styled } from "@mui/material/styles";
import { usePdfSettings } from "../../layouts/PdfSettingsContext.js";
import { decimalOnly } from "../../utils/numericInput";

import {
  Add,
  Delete,
  Edit,
  Visibility,
  Close,
  CheckCircle,
  Cancel,
  FilterList,
  Search,
  Warning,
  Print,
  PlaylistAdd as BulkAddIcon,
} from "@mui/icons-material";

/* =========================================================
   BASE URL
========================================================= */

const API_BASE_URL =
  "https://wims.revisetoexcel.com/api";

/* =========================================================
   AXIOS
========================================================= */

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

/* =========================================================
   TOKEN
========================================================= */

const getAccessToken = () => {
  if (typeof window === "undefined") {
    return null;
  }

  const tokenKeys = [
    "accessToken",
    "access_token",
    "token",
  ];

  for (const key of tokenKeys) {
    const localToken =
      localStorage.getItem(key);

    if (localToken) {
      return localToken;
    }

    const sessionToken =
      sessionStorage.getItem(key);

    if (sessionToken) {
      return sessionToken;
    }
  }

  return null;
};

/* =========================================================
   INTERCEPTOR
========================================================= */

api.interceptors.request.use(
  (config) => {
    const token = getAccessToken();

    if (token) {
      config.headers =
        config.headers || {};

      config.headers.Authorization =
        `Bearer ${token}`;
    }

    return config;
  },
  (error) =>
    Promise.reject(error)
);

/* =========================================================
   ENDPOINTS
========================================================= */

const ENDPOINTS = {
  bookIssueList:
    "/library/book-issue/list/",

  bookIssueCreate:
    "/library/book-issue/create/",

  bookIssueBulkCreate:
    "/library/book-issue/bulk-create/",

  bookIssueDetail: (id) =>
    `/library/book-issue/${id}/`,

  bookIssueUpdate: (id) =>
    `/library/book-issue/update/${id}/`,

  bookIssueDelete: (id) =>
    `/library/book-issue/delete/${id}/`,

  bookIssueStatus: (id) =>
    `/library/book-issue/status/${id}/`,

  memberDropdown:
    "/library/library-member/dropdown/",

  bookDropdown:
    "/library/book-master/dropdown/",
};

/* =========================================================
   SHARED SCROLLBAR
========================================================= */

const scrollbarStyles = (theme) => {
  const dark = theme.palette.mode === "dark";

  return {
   '&::-webkit-scrollbar': {
    height: '6px',
    background: 'transparent',
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)',
    borderRadius: '3px',
  },
  '&::-webkit-scrollbar-thumb:hover': {
    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)',
  },
  scrollbarWidth: 'thin',
  scrollbarColor: `${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)'} transparent`,
    
    scrollbarWidth: "thin",
  };
};

const CustomScrollbar = styled("div")(
  ({ theme }) => ({
    overflowY: "auto",
    backgroundColor:
      theme.palette.background.paper,
    ...scrollbarStyles(theme),
  })
);

/* =========================================================
   USER
========================================================= */

const getStoredUser = () => {
  if (typeof window === "undefined") {
    return {};
  }

  try {
    return JSON.parse(
      sessionStorage.getItem("user") || "{}"
    );
  } catch {
    return {};
  }
};

/* =========================================================
   DATE
========================================================= */

const getToday = () =>
  new Date().toISOString().split("T")[0];

const getDatePlusDays = (dateString, days) => {
  if (!dateString) return "";

  const date = new Date(`${dateString}T00:00:00`);
  date.setDate(date.getDate() + days);

  return date.toISOString().split("T")[0];
};

/* =========================================================
   EMPTY FORM
========================================================= */

const getEmptyForm = () => {
  const issueDate = getToday();

  return {
    library_member: "",
    book: "",

    issue_date: issueDate,
    due_date: getDatePlusDays(issueDate, 7),
    return_date: "",

    overdue_days: 0,
    fine_amount: "0",
    fine_status: "pending",

    book_condition: "good",

    remarks: "",

    transaction_status: "issued",

    is_active: true,

    received_by: "",
  };
};

const EMPTY_BULK_ROW = {
  library_member: "",
  book: "",
  issue_date: getToday(),
  due_date: "",
  remarks: "",
};

/* =========================================================
   NORMALIZE ARRAY
========================================================= */

const normalizeArray = (data) => {
  if (Array.isArray(data)) {
    return data;
  }

  if (Array.isArray(data?.results)) {
    return data.results;
  }

  if (Array.isArray(data?.data)) {
    return data.data;
  }

  if (Array.isArray(data?.records)) {
    return data.records;
  }

  if (Array.isArray(data?.items)) {
    return data.items;
  }

  if (Array.isArray(data?.transactions)) {
    return data.transactions;
  }

  return [];
};

/* =========================================================
   COMPONENT
========================================================= */

export default function BookIssueTransaction() {
  const theme = useTheme();
  const { pdfThemeColor, pdfTextColor } = usePdfSettings();

  // Global branch (navbar)
  const { selectedBranchId } = useBranch();

  const isDark = theme.palette.mode === "dark";

  const user = useMemo(
    () => getStoredUser(),
    []
  );

  const organizationId =
    user?.organization_id ||
    user?.organization?.id ||
    "";

  /* =======================================================
     COLORS
  ======================================================= */

  const textColor = isDark ? "#f1f5f9" : "#1f2937";

  const secondaryTextColor = isDark ? "#cbd5e1" : "#6b7280";

  const modalTextColor = isDark ? "#f1f5f9" : "#374151";

  const borderColor = isDark
    ? "rgba(255,255,255,0.16)"
    : "rgba(0,0,0,0.15)";

  const tableRowHover = isDark
    ? "rgba(255,255,255,0.05)"
    : "rgba(0,0,0,0.04)";

  const inputBackground = isDark
    ? "rgba(255,255,255,0.06)"
    : "#ffffff";

  /* =======================================================
     SELECT STYLES
  ======================================================= */

  const inputLabelSx = {
    color: isDark
      ? "rgba(255,255,255,0.75)"
      : "#4b5563",

    "&.Mui-focused": {
      color: theme.palette.primary.main,
    },
  };

  const selectSx = {
    color: textColor,
    backgroundColor: inputBackground,

    "& .MuiSelect-select": {
      color: `${textColor} !important`,
      minHeight: "40px",
      display: "flex",
      alignItems: "center",
    },

    "& .MuiSvgIcon-root": {
      color: `${textColor} !important`,
    },

    "& .MuiOutlinedInput-notchedOutline": {
      borderColor,
    },

    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.palette.primary.main,
    },

    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.palette.primary.main,
    },
  };

  const textFieldSx = {
    "& .MuiInputLabel-root": {
      color: isDark
        ? "rgba(255,255,255,0.75)"
        : "#4b5563",
    },

    "& .MuiInputLabel-root.Mui-focused": {
      color: theme.palette.primary.main,
    },

    "& .MuiOutlinedInput-root": {
      color: textColor,
      backgroundColor: inputBackground,

      "& fieldset": {
        borderColor,
      },

      "&:hover fieldset": {
        borderColor: theme.palette.primary.main,
      },

      "&.Mui-focused fieldset": {
        borderColor: theme.palette.primary.main,
      },
    },

    "& input": {
      color: textColor,
    },

    "& textarea": {
      color: textColor,
    },

    // Dark-mode date picker icon
    "& input[type=\"date\"]": {
      colorScheme: isDark ? "dark" : "light",
    },
  };

  const menuProps = {
    PaperProps: {
      sx: {
        backgroundColor: theme.palette.background.paper,
        color: textColor,
        ...scrollbarStyles(theme),

        "& .MuiMenuItem-root": {
          color: textColor,
        },

        "& .MuiMenuItem-root:hover": {
          backgroundColor: tableRowHover,
        },

        "& .MuiMenuItem-root.Mui-selected": {
          backgroundColor:
            theme.palette.action.selected,
          color: textColor,
        },
      },
    },
  };

  /* =======================================================
     LIST
  ======================================================= */

  const [rows, setRows] = useState([]);

  const [loading, setLoading] = useState(false);

  const [totalCount, setTotalCount] = useState(0);

  const [page, setPage] = useState(0);

  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [searchInput, setSearchInput] = useState("");

  const [debouncedSearch, setDebouncedSearch] =
    useState("");

  /* =======================================================
     FILTERS
  ======================================================= */

  const [filters, setFilters] = useState({
    member_type: "",
    library_member: "",
    book: "",
    transaction_status: "",
    fine_status: "",
    book_condition: "",

    issue_date_from: "",
    issue_date_to: "",

    due_date_from: "",
    due_date_to: "",

    is_active: "",
  });

  /* =======================================================
     DROPDOWNS
  ======================================================= */

  const [members, setMembers] = useState([]);

  const [books, setBooks] = useState([]);

  /* =======================================================
     MODALS
  ======================================================= */

  const [openDialog, setOpenDialog] = useState(false);

  const [dialogMode, setDialogMode] = useState("create");

  const [selectedId, setSelectedId] = useState(null);

  const [form, setForm] = useState(getEmptyForm());

  const [formLoading, setFormLoading] = useState(false);

  const [openFilterDialog, setOpenFilterDialog] =
    useState(false);

  /* =======================================================
     BULK
  ======================================================= */

  const [openBulkDialog, setOpenBulkDialog] =
    useState(false);

  const [bulkRows, setBulkRows] = useState([
    { ...EMPTY_BULK_ROW },
  ]);

  /* =======================================================
     CONFIRMATION MODAL
  ======================================================= */

  const [confirmModal, setConfirmModal] = useState({
    open: false,
    type: "",
    id: null,
    row: null,
  });

  const [confirmLoading, setConfirmLoading] =
    useState(false);

  /* =======================================================
     MESSAGES
  ======================================================= */

  const [error, setError] = useState("");

  const [success, setSuccess] = useState("");

  /* =======================================================
     ERROR
  ======================================================= */

  const getErrorMessage = (err, fallback) => {
    console.error("API ERROR:", err);

    if (err?.response?.status === 401) {
      return "Authentication failed. Please login again.";
    }

    if (err?.response?.status === 403) {
      return "You do not have permission to access this API.";
    }

    if (err?.response?.status === 404) {
      return "API endpoint not found (404). Please check the backend API URL.";
    }

    const data = err?.response?.data;

    if (!data) {
      return err?.message || fallback;
    }

    if (typeof data === "string") {
      return data;
    }

    if (data.detail) {
      return String(data.detail);
    }

    if (data.message) {
      return typeof data.message === "string"
        ? data.message
        : JSON.stringify(data.message);
    }

    if (data.error) {
      return typeof data.error === "string"
        ? data.error
        : JSON.stringify(data.error);
    }

    if (data.errors && typeof data.errors === "object") {
      const messages = [];

      Object.entries(data.errors).forEach(
        ([field, value]) => {
          if (Array.isArray(value)) {
            value.forEach((message) => {
              messages.push(
                field === "non_field_errors"
                  ? message
                  : `${field}: ${message}`
              );
            });
          } else if (typeof value === "string") {
            messages.push(`${field}: ${value}`);
          }
        }
      );

      if (messages.length) {
        return messages.join(" | ");
      }
    }

    try {
      return JSON.stringify(data);
    } catch {
      return fallback;
    }
  };

  /* =======================================================
     FETCH LIST
  ======================================================= */

  const fetchBookIssues = useCallback(async () => {
    if (!organizationId || !selectedBranchId) {
      setRows([]);
      setTotalCount(0);
      return;
    }

    try {
      setLoading(true);
      setError("");

      const params = {
        organization: Number(organizationId),
        branch: Number(selectedBranchId),
      };

      if (debouncedSearch) {
        params.search = debouncedSearch;
      }

      Object.entries(filters).forEach(([key, value]) => {
        if (value !== "" && value !== null && value !== undefined) {
          params[key] = value;
        }
      });

      const response = await api.get(
        ENDPOINTS.bookIssueList,
        { params }
      );

      const data = response.data;

      const list = normalizeArray(data);

      setRows(list);

      setTotalCount(
        data?.count ??
          data?.total ??
          data?.total_count ??
          list.length
      );
    } catch (err) {
      setError(
        getErrorMessage(
          err,
          "Failed to fetch book issue transactions."
        )
      );

      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [
    organizationId,
    selectedBranchId,
    debouncedSearch,
    filters,
  ]);

  useEffect(() => {
    fetchBookIssues();
  }, [fetchBookIssues, page, rowsPerPage]);

  // Debounce search
  useEffect(() => {
    const t = setTimeout(
      () => setDebouncedSearch(searchInput.trim()),
      400
    );

    return () => clearTimeout(t);
  }, [searchInput]);

  /* =======================================================
     FETCH MEMBERS
  ======================================================= */

  const fetchMembers = useCallback(
    async (memberType = "") => {
      if (!organizationId || !selectedBranchId) {
        setMembers([]);
        return;
      }

      try {
        const params = {
          organization: Number(organizationId),
          branch: Number(selectedBranchId),
        };

        if (memberType) {
          params.member_type = memberType;
        }

        const response = await api.get(
          ENDPOINTS.memberDropdown,
          { params }
        );

        setMembers(normalizeArray(response.data));
      } catch {
        setMembers([]);
      }
    },
    [organizationId, selectedBranchId]
  );

  /* =======================================================
     FETCH BOOKS
  ======================================================= */

  const fetchBooks = useCallback(async () => {
    if (!organizationId || !selectedBranchId) {
      setBooks([]);
      return;
    }

    try {
      const params = {
        organization: Number(organizationId),
        branch: Number(selectedBranchId),
      };

      const response = await api.get(
        ENDPOINTS.bookDropdown,
        { params }
      );

      setBooks(normalizeArray(response.data));
    } catch {
      setBooks([]);
    }
  }, [organizationId, selectedBranchId]);

  // Load dropdowns whenever the navbar branch changes
  useEffect(() => {
    if (!selectedBranchId) {
      setMembers([]);
      setBooks([]);
      return;
    }

    fetchMembers(filters.member_type);
    fetchBooks();
  }, [
    selectedBranchId,
    fetchMembers,
    fetchBooks,
    filters.member_type,
  ]);

  // Reset page when filters change
  useEffect(() => {
    setPage(0);
  }, [debouncedSearch, rowsPerPage]);

  /* =======================================================
     FILTER CHANGE
  ======================================================= */

  const handleFilterChange = (name, value) => {
    setFilters((prev) => {
      const updated = {
        ...prev,
        [name]: value,
      };

      if (name === "member_type") {
        updated.library_member = "";
      }

      return updated;
    });
  };

  /* =======================================================
     FILTER APPLY
  ======================================================= */

  const handleApplyFilters = () => {
    setPage(0);
    setOpenFilterDialog(false);
    fetchBookIssues();
  };

  /* =======================================================
     RESET
  ======================================================= */

  const handleReset = () => {
    setFilters({
      member_type: "",
      library_member: "",
      book: "",
      transaction_status: "",
      fine_status: "",
      book_condition: "",

      issue_date_from: "",
      issue_date_to: "",

      due_date_from: "",
      due_date_to: "",

      is_active: "",
    });

    setSearchInput("");
    setPage(0);

    fetchBookIssues();

    setOpenFilterDialog(false);
  };

  /* =======================================================
     ACTIVE FILTER COUNT
  ======================================================= */

  const activeFilterCount = useMemo(() => {
    const filterKeys = [
      "member_type",
      "library_member",
      "book",
      "transaction_status",
      "fine_status",
      "book_condition",
      "issue_date_from",
      "issue_date_to",
      "due_date_from",
      "due_date_to",
      "is_active",
    ];

    return filterKeys.filter(
      (key) =>
        filters[key] !== "" &&
        filters[key] !== null &&
        filters[key] !== undefined
    ).length;
  }, [filters]);

  /* =======================================================
     FORM CHANGE
  ======================================================= */

  const handleFormChange = (name, value) => {
    setForm((prev) => {
      const updated = {
        ...prev,
        [name]: value,
      };

      // Auto-select Due Date as 7 days after Issue Date
      if (name === "issue_date") {
        updated.due_date = getDatePlusDays(value, 7);
      }

      // Return Date is only applicable to Returned transactions
      if (
        name === "transaction_status" &&
        value !== "returned"
      ) {
        updated.return_date = "";
      }

      return updated;
    });
  };

  /* =======================================================
     CREATE
  ======================================================= */

  const handleCreate = () => {
    setDialogMode("create");
    setSelectedId(null);
    setForm(getEmptyForm());
    setError("");
    setOpenDialog(true);
  };

  /* =======================================================
     DETAIL
  ======================================================= */

  const fetchDetail = async (id, mode = "view") => {
    try {
      setFormLoading(true);
      setError("");

      const response = await api.get(
        ENDPOINTS.bookIssueDetail(id)
      );

      const d = response.data?.data ?? response.data;

      setForm({
        library_member: String(d.library_member ?? ""),

        book: String(d.book ?? ""),

        issue_date: d.issue_date
          ? d.issue_date.slice(0, 10)
          : "",

        due_date: d.due_date
          ? d.due_date.slice(0, 10)
          : "",

        return_date: d.return_date
          ? d.return_date.slice(0, 10)
          : "",

        overdue_days: d.overdue_days ?? 0,

        fine_amount: d.fine_amount ?? "0.00",

        fine_status: d.fine_status || "pending",

        book_condition: d.book_condition || "good",

        remarks: d.remarks || "",

        transaction_status:
          d.transaction_status || "issued",

        is_active: d.is_active ?? true,

        received_by: d.received_by
          ? String(d.received_by)
          : "",
      });

      setSelectedId(id);

      setDialogMode(mode);

      setOpenDialog(true);
    } catch (err) {
      setError(
        getErrorMessage(
          err,
          "Failed to fetch transaction details."
        )
      );
    } finally {
      setFormLoading(false);
    }
  };

  /* =======================================================
     PRINT PDF
  ======================================================= */

  const handlePrintPdf = async () => {
    if (!form || !selectedId) {
      return;
    }

    const selectedRow = rows.find(
      (row) => String(row.id) === String(selectedId)
    );

    const doc = new jsPDF();

    const pageWidth = doc.internal.pageSize.getWidth();

    const pageHeight = doc.internal.pageSize.getHeight();

    const margin = 15;

    const member = members.find(
      (item) =>
        String(item.id) === String(form.library_member)
    );

    const book = books.find(
      (item) => String(item.id) === String(form.book)
    );

    const formatDate = (value) => {
      if (!value) {
        return "—";
      }

      const date = new Date(value);

      if (Number.isNaN(date.getTime())) {
        return String(value);
      }

      return date.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
    };

    const safeValue = (value) => {
      if (value === null || value === undefined || value === "") {
        return "—";
      }

      return String(value);
    };

    const organizationName =
      selectedRow?.organization_name || "Organization";

    const branchName =
      selectedRow?.branch_name || "Branch";

    const hexToRgb = (hex) => [
      parseInt(hex.slice(1, 3), 16),
      parseInt(hex.slice(3, 5), 16),
      parseInt(hex.slice(5, 7), 16),
    ];
    const backgroundRgb = hexToRgb(pdfThemeColor);
    const textRgb = hexToRgb(pdfTextColor);

    doc.setFillColor(...backgroundRgb);
    doc.rect(0, 0, pageWidth, 46, "F");

    try {
      doc.addImage(
        aligarhLogo,
        "PNG",
        margin - 2,
        5,
        50,
        30
      );
    } catch (logoError) {
      console.error("Unable to add PDF logo:", logoError);
    }

    doc.setFont("helvetica", "bold");
    doc.setFontSize(18);
    doc.setTextColor(255, 255, 255);
    doc.text(organizationName, pageWidth / 2, 14, {
      align: "center",
    });

    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.setTextColor(255, 255, 255);
    doc.text("Book Issue Transaction", pageWidth / 2, 22, {
      align: "center",
    });

    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(...textRgb);
    doc.text(
      `Transaction Code: ${selectedRow?.transaction_code || selectedId}`,
      20,
      38
    );

    doc.line(20, 42, pageWidth - 20, 42);

    const details = [
      ["Branch", branchName],
      [
        "Library Member",
        selectedRow?.member_name ||
          (member ? getMemberLabel(member) : "—"),
      ],
      [
        "Member ID",
        selectedRow?.member_id || "—",
      ],
      [
        "Book",
        selectedRow?.book_title ||
          (book ? getBookLabel(book) : "—"),
      ],
      ["Book Code", selectedRow?.book_code || "—"],
      ["Issue Date", formatDate(form.issue_date)],
      ["Due Date", formatDate(form.due_date)],
      ["Return Date", formatDate(form.return_date)],
      ["Overdue Days", safeValue(form.overdue_days)],
      ["Fine Amount", safeValue(form.fine_amount)],
      ["Fine Status", safeValue(form.fine_status)],
      ["Book Condition", safeValue(form.book_condition)],
      ["Transaction Status", safeValue(form.transaction_status)],
      ["Active Status", form.is_active ? "Active" : "Inactive"],
      ["Remarks", safeValue(form.remarks)],
    ];

    let y = 52;

    const labelX = 20;
    const valueX = 68;
    const valueWidth = pageWidth - valueX - 20;

    details.forEach(([label, value]) => {
      if (y > pageHeight - 25) {
        doc.addPage();
        y = 20;
      }

      doc.setFont("helvetica", "bold");
      doc.setFontSize(10);
      doc.setTextColor(...textRgb);
      doc.text(`${label}:`, labelX, y);

      doc.setFont("helvetica", "normal");
      doc.setTextColor(...textRgb);

      const wrappedText = doc.splitTextToSize(
        String(value),
        valueWidth
      );

      doc.text(wrappedText, valueX, y);

      y += Math.max(8, wrappedText.length * 5);
    });

    const pageCount = doc.internal.getNumberOfPages();

    for (
      let pageNumber = 1;
      pageNumber <= pageCount;
      pageNumber += 1
    ) {
      doc.setPage(pageNumber);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      doc.setTextColor(...textRgb);
      doc.text(
        `Page ${pageNumber} of ${pageCount}`,
        pageWidth / 2,
        pageHeight - 10,
        {
          align: "center",
        }
      );
    }

    doc.save(`Book-Issue-${selectedId}.pdf`);
  };

  /* =======================================================
     CREATE SUBMIT
  ======================================================= */

  const validateIssueForm = () => {
    if (!organizationId) {
      return "Organization not found in session";
    }

    if (!selectedBranchId) {
      return "Please select a branch from the navbar";
    }

    if (!form.library_member) {
      return "Please select a library member";
    }

    if (!form.book) {
      return "Please select a book";
    }

    if (!form.issue_date) {
      return "Issue date is required";
    }

    if (!form.due_date) {
      return "Due date is required";
    }

    if (form.due_date < form.issue_date) {
      return "Due date cannot be before issue date";
    }

    return null;
  };

  const handleSubmit = async () => {
    const validationError = validateIssueForm();

    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      setFormLoading(true);
      setError("");
      setSuccess("");

      const payload = {
        organization: Number(organizationId),
        branch: Number(selectedBranchId),
        library_member: Number(form.library_member),
        book: Number(form.book),
        issue_date: form.issue_date,
        due_date: form.due_date,
        book_condition: form.book_condition || "good",
        remarks: form.remarks || null,
      };

      await api.post(ENDPOINTS.bookIssueCreate, payload);

      setSuccess("Book issued successfully.");

      setOpenDialog(false);

      fetchBookIssues();
    } catch (err) {
      setError(
        getErrorMessage(err, "Failed to issue book.")
      );
    } finally {
      setFormLoading(false);
    }
  };

  /* =======================================================
     UPDATE
  ======================================================= */

  const handleUpdate = async () => {
    if (!form.issue_date || !form.due_date) {
      setError("Issue Date and Due Date are required.");
      return;
    }

    if (
      form.transaction_status === "returned" &&
      !form.return_date
    ) {
      setError(
        "Return Date is required when transaction status is Returned."
      );

      return;
    }

    if (form.due_date < form.issue_date) {
      setError("Due date cannot be before issue date");
      return;
    }

    try {
      setFormLoading(true);
      setError("");
      setSuccess("");

      const payload = {
        organization: Number(organizationId),
        branch: Number(selectedBranchId),
        library_member: Number(form.library_member),
        book: Number(form.book),
        issue_date: form.issue_date,
        due_date: form.due_date,
        return_date: form.return_date || null,
        overdue_days: Number(form.overdue_days || 0),
        fine_amount: form.fine_amount || "0",
        fine_status: form.fine_status || "pending",
        book_condition: form.book_condition || "good",
        remarks: form.remarks || null,
        transaction_status: form.transaction_status || "issued",
        is_active: Boolean(form.is_active),
        received_by: form.received_by
          ? Number(form.received_by)
          : null,
      };

      await api.put(
        ENDPOINTS.bookIssueUpdate(selectedId),
        payload
      );

      setSuccess("Transaction updated successfully.");

      setOpenDialog(false);

      fetchBookIssues();
    } catch (err) {
      setError(
        getErrorMessage(err, "Failed to update transaction.")
      );
    } finally {
      setFormLoading(false);
    }
  };

  /* =======================================================
     BULK ISSUE
  ======================================================= */

  const handleOpenBulkDialog = () => {
    const issueDate = getToday();

    setBulkRows([
      {
        ...EMPTY_BULK_ROW,
        issue_date: issueDate,
        due_date: getDatePlusDays(issueDate, 7),
      },
    ]);

    setOpenBulkDialog(true);
  };

  const updateBulkRow = (i, field, value) =>
    setBulkRows((rows) =>
      rows.map((r, idx) =>
        idx === i ? { ...r, [field]: value } : r
      )
    );

  const addBulkRow = () => {
    const issueDate = getToday();

    setBulkRows((rows) => [
      ...rows,
      {
        ...EMPTY_BULK_ROW,
        issue_date: issueDate,
        due_date: getDatePlusDays(issueDate, 7),
      },
    ]);
  };

  const removeBulkRow = (i) =>
    setBulkRows((rows) =>
      rows.length === 1
        ? rows
        : rows.filter((_, idx) => idx !== i)
    );

  const handleBulkSubmit = async () => {
    if (!organizationId) {
      setSnackbarError(
        "Organization not found in session"
      );
      return;
    }

    if (!selectedBranchId) {
      setSnackbarError(
        "Please select a branch from the navbar"
      );
      return;
    }

    for (let i = 0; i < bulkRows.length; i++) {
      const r = bulkRows[i];
      const n = i + 1;

      if (!r.library_member) {
        setSnackbarError(
          `Transaction ${n}: please select a library member`
        );
        return;
      }

      if (!r.book) {
        setSnackbarError(
          `Transaction ${n}: please select a book`
        );
        return;
      }

      if (!r.issue_date) {
        setSnackbarError(
          `Transaction ${n}: issue date is required`
        );
        return;
      }

      if (!r.due_date) {
        setSnackbarError(
          `Transaction ${n}: due date is required`
        );
        return;
      }

      if (r.due_date < r.issue_date) {
        setSnackbarError(
          `Transaction ${n}: due date cannot be before issue date`
        );
        return;
      }
    }

    // No duplicate Member + Book pairs in one request
    const keys = bulkRows.map(
      (r) => `${r.library_member}|${r.book}`
    );

    if (new Set(keys).size !== keys.length) {
      setSnackbarError(
        "Duplicate Member + Book combinations found in the list"
      );
      return;
    }

    const payload = {
      organization: Number(organizationId),
      branch: Number(selectedBranchId),
      transactions: bulkRows.map((r) => ({
        library_member: Number(r.library_member),
        book: Number(r.book),
        issue_date: r.issue_date,
        due_date: r.due_date,
        remarks: r.remarks || null,
      })),
    };

    try {
      setFormLoading(true);
      setSuccess("");
      setError("");

      const response = await api.post(
        ENDPOINTS.bookIssueBulkCreate,
        payload
      );

      const result = response.data;

      console.log("BULK ISSUE RESPONSE:", result);

      setSuccess(
        result?.message ||
          "Books issued in bulk successfully."
      );

      setOpenBulkDialog(false);

      fetchBookIssues();
    } catch (err) {
      setError(
        getErrorMessage(err, "Failed to bulk issue books.")
      );
    } finally {
      setFormLoading(false);
    }
  };

  const setSnackbarError = (message) => {
    setError(message);
  };

  /* =======================================================
     OPEN DELETE CONFIRMATION
  ======================================================= */

  const openDeleteConfirmation = (id) => {
    setConfirmModal({
      open: true,
      type: "delete",
      id,
      row: null,
    });
  };

  /* =======================================================
     OPEN STATUS CONFIRMATION
  ======================================================= */

  const openStatusConfirmation = (row) => {
    setConfirmModal({
      open: true,
      type: "status",
      id: row.id,
      row,
    });
  };

  /* =======================================================
     CLOSE CONFIRMATION
  ======================================================= */

  const closeConfirmation = () => {
    if (confirmLoading) return;

    setConfirmModal({
      open: false,
      type: "",
      id: null,
      row: null,
    });
  };

  /* =======================================================
     CONFIRM DELETE / STATUS
  ======================================================= */

  const handleConfirmAction = async () => {
    if (!confirmModal.id) {
      return;
    }

    try {
      setConfirmLoading(true);
      setError("");
      setSuccess("");

      if (confirmModal.type === "delete") {
        await api.delete(
          ENDPOINTS.bookIssueDelete(confirmModal.id)
        );

        setSuccess(
          "Transaction deleted successfully."
        );
      }

      if (confirmModal.type === "status") {
        const currentRow = confirmModal.row;

        const nextStatus = !currentRow.is_active;

        await api.patch(
          ENDPOINTS.bookIssueStatus(confirmModal.id),
          {
            is_active: nextStatus,
          }
        );

        setSuccess(
          `Transaction ${
            nextStatus ? "activated" : "deactivated"
          } successfully.`
        );
      }

      closeConfirmation();

      fetchBookIssues();
    } catch (err) {
      setError(
        getErrorMessage(
          err,
          confirmModal.type === "delete"
            ? "Failed to delete transaction."
            : "Failed to update transaction status."
        )
      );
    } finally {
      setConfirmLoading(false);
    }
  };

  /* =======================================================
     LABELS
  ======================================================= */

  const getMemberLabel = (member) => {
    if (typeof member === "string") {
      return member;
    }

    if (member && typeof member === "object") {
      return (
        member.label ||
        member.member_name ||
        member.name ||
        member.full_name ||
        member.member_code ||
        member.member_id ||
        member.id ||
        ""
      );
    }

    return "";
  };

  const getBookLabel = (book) => {
    if (typeof book === "string") {
      return book;
    }

    if (book && typeof book === "object") {
      return (
        book.display_name ||
        book.label ||
        book.book_title ||
        book.title ||
        book.book_name ||
        book.book_code ||
        book.id ||
        ""
      );
    }

    return "";
  };

  /* =======================================================
     STATUS CHIP
  ======================================================= */

  const statusChip = (status, display) => {
    const value = status?.toLowerCase?.() ?? "";

    return (
      <Chip
        size="small"
        label={display || status || "-"}
        sx={{
          fontWeight: 600,
          borderRadius: "20px",

          backgroundColor:
            value === "issued"
              ? "rgba(25,118,210,0.12)"
              : value === "returned"
              ? "rgba(46,125,50,0.12)"
              : value === "lost"
              ? "rgba(211,47,47,0.12)"
              : "rgba(237,108,2,0.12)",

          color:
            value === "issued"
              ? theme.palette.primary.main
              : value === "returned"
              ? theme.palette.success.main
              : value === "lost"
              ? theme.palette.error.main
              : theme.palette.warning.main,

          border: "none",
        }}
      />
    );
  };

  /* =======================================================
     CONDITION CHIP
  ======================================================= */

  const conditionChip = (condition, display) => {
    const value = condition?.toLowerCase?.() ?? "";

    const isGood = value === "good";

    const isDamaged = value === "damaged";

    const isLost = value === "lost";

    return (
      <Chip
        size="small"
        label={display || condition || "-"}
        sx={{
          fontWeight: 600,
          borderRadius: "20px",

          backgroundColor: isGood
            ? "rgba(46,125,50,0.12)"
            : isDamaged
            ? "rgba(237,108,2,0.12)"
            : isLost
            ? "rgba(211,47,47,0.12)"
            : "rgba(108,117,125,0.12)",

          color: isGood
            ? theme.palette.success.main
            : isDamaged
            ? theme.palette.warning.main
            : isLost
            ? theme.palette.error.main
            : theme.palette.text.secondary,

          border: "none",
        }}
      />
    );
  };

  /* =======================================================
     SUCCESS ALERT STYLE
  ======================================================= */

  const successAlertSx = {
    mb: 2,

    ...(isDark && {
      backgroundColor: "#ffffff",
      color: "#111827",

      "& .MuiAlert-icon": {
        color: theme.palette.success.main,
      },

      "& .MuiAlert-message": {
        color: "#111827",
        fontWeight: 600,
      },

      "& .MuiAlert-action": {
        color: "#111827",
      },
    }),
  };

  /* =========================================================
     RENDER
  ========================================================= */

  return (
    <Box
      p={{ xs: 1, md: 3 }}
      sx={{ color: textColor }}
    >
      {/* =====================================================
          HEADER
      ===================================================== */}

      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
        gap={2}
        flexWrap="wrap"
      >
        {/* SEARCH */}

        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1.5}
          flex={1}
          maxWidth={{ xs: "100%", md: 800 }}
        >
          <TextField
  variant="outlined"
  placeholder="Search transaction..."
  value={searchInput}
  onChange={(e) => setSearchInput(e.target.value)}
  sx={{
    width: 350,
    maxWidth: "100%",
    minWidth: { xs: "100%", sm: 280 },
    flex: 1,

    "& .MuiOutlinedInput-root": {
      height: 50,
      borderRadius: "30px",
      backgroundColor: inputBackground,

      "& fieldset": {
        borderColor: borderColor,
      },

      "&:hover fieldset": {
        borderColor: theme.palette.primary.main,
      },

      "&.Mui-focused fieldset": {
        borderColor: theme.palette.primary.main,
        borderWidth: 2,
      },
    },

    "& .MuiInputBase-input": {
      color: textColor,
    },

    "& .MuiInputBase-input::placeholder": {
      color: secondaryTextColor,
      opacity: 1,
    },
  }}
  InputProps={{
    startAdornment: (
      <InputAdornment position="start">
        <Search
          sx={{
            color: theme.palette.primary.main,
            ml: 1,
          }}
        />
      </InputAdornment>
    ),

    endAdornment: searchInput ? (
      <InputAdornment position="end">
        <IconButton
          size="small"
          onClick={() => {
            setSearchInput("");
            setDebouncedSearch("");
          }}
        >
          <CloseIcon
            fontSize="small"
            sx={{ color: secondaryTextColor }}
          />
        </IconButton>
      </InputAdornment>
    ) : null,
  }}
/>
        </Stack>

        {/* ACTIONS */}

        <Stack
          direction="row"
          spacing={1.5}
          alignItems="center"
        >
          {/* FILTER */}

          <Button
            variant="contained"
            startIcon={<FilterList />}
            onClick={() => setOpenFilterDialog(true)}
            sx={{
              minWidth: 125,
              height: 40,
              borderRadius: 2,
              fontWeight: 700,
            }}
          >
            Filters

            {activeFilterCount > 0 && (
              <Chip
                label={activeFilterCount}
                size="small"
                color="primary"
                sx={{
                  ml: 1,
                  height: 22,
                  minWidth: 22,
                  fontWeight: 700,
                }}
              />
            )}
          </Button>

          {/* BULK ISSUE */}

          <Button
            variant="contained"
            startIcon={<BulkAddIcon />}
            onClick={handleOpenBulkDialog}
            sx={{
              minWidth: 150,
              height: 40,
              borderRadius: 2,
              fontWeight: 700,
            }}
          >
            BULK ISSUE
          </Button>

          {/* ISSUE BOOK */}

          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleCreate}
            sx={{
              minWidth: 130,
              height: 40,
              borderRadius: 2,
              fontWeight: 700,
            }}
          >
            ISSUE BOOK
          </Button>
        </Stack>
      </Box>

      {/* =====================================================
          ALERTS
      ===================================================== */}

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          onClose={() => setError("")}
        >
          {error}
        </Alert>
      )}

      {success && (
        <Alert
          severity="success"
          sx={successAlertSx}
          onClose={() => setSuccess("")}
        >
          {success}
        </Alert>
      )}

      {/* =====================================================
          FILTER MODAL
      ===================================================== */}

      <Modal
        open={openFilterDialog}
        onClose={() => !loading && setOpenFilterDialog(false)}
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",

            width: {
              xs: "94%",
              sm: 680,
              md: 850,
            },

            maxWidth: "95%",
            maxHeight: "90vh",
            display: "flex",
            flexDirection: "column",

            bgcolor: theme.palette.background.paper,
            color: modalTextColor,

            borderRadius: 3,
            boxShadow: 24,
            overflow: "hidden",
            outline: "none",
          }}
        >
          {/* FILTER HEADER */}

          <Box
            sx={{
              flexShrink: 0,
              backgroundColor: theme.palette.primary.main,
              px: 2,
              py: 1.5,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Box>
              <Typography
                variant="h6"
                sx={{ color: "#ffffff", fontWeight: 700 }}
              >
                Filters
              </Typography>

              <Typography
                variant="body2"
                sx={{ color: "rgba(255,255,255,0.85)" }}
              >
                Filter book issue transactions
              </Typography>
            </Box>

            <IconButton
              onClick={() =>
                !loading && setOpenFilterDialog(false)
              }
              sx={{ color: "#ffffff" }}
            >
              <Close />
            </IconButton>
          </Box>

          {/* FILTER BODY (scrollable) */}

          <CustomScrollbar
            style={{
              flex: 1,
              minHeight: 0,
              padding: "24px 24px 8px",
            }}
          >
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(2, minmax(0, 1fr))",
                },
                gap: 2,
              }}
            >
              {/* MEMBER TYPE */}

              <FormControl
                fullWidth
                size="small"
                sx={{
                  "& .MuiInputLabel-root": inputLabelSx,
                }}
              >
                <InputLabel>Member Type</InputLabel>

                <Select
                  label="Member Type"
                  value={filters.member_type || ""}
                  onChange={(e) =>
                    handleFilterChange(
                      "member_type",
                      e.target.value
                    )
                  }
                  sx={selectSx}
                  MenuProps={menuProps}
                >
                  <MenuItem value="">All</MenuItem>

                  <MenuItem value="student">Student</MenuItem>

                  <MenuItem value="staff">Staff</MenuItem>
                </Select>
              </FormControl>

              {/* SEARCHABLE LIBRARY MEMBER FILTER */}
              <Autocomplete
                fullWidth
                size="small"
                options={members}
                value={members.find((member) => String(member.id) === String(filters.library_member)) || null}
                onChange={(_, option) =>
                  handleFilterChange("library_member", option ? String(option.id) : "")
                }
                getOptionLabel={(option) => String(getMemberLabel(option) || "")}
                isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
                noOptionsText="No library members found"
                clearOnEscape
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Library Member"
                    placeholder="Search members..."
                    sx={{ ...textFieldSx, "& .MuiInputLabel-root": inputLabelSx }}
                  />
                )}
                sx={{
                  "& .MuiAutocomplete-inputRoot": { color: textColor, backgroundColor: inputBackground },
                  "& .MuiOutlinedInput-notchedOutline": { borderColor },
                  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: theme.palette.primary.main },
                  "& .MuiSvgIcon-root": { color: textColor },
                }}
              />

              {/* SEARCHABLE BOOK FILTER */}
              <Autocomplete
                fullWidth
                size="small"
                options={books}
                value={books.find((book) => String(book.id) === String(filters.book)) || null}
                onChange={(_, option) =>
                  handleFilterChange("book", option ? String(option.id) : "")
                }
                getOptionLabel={(option) => String(getBookLabel(option) || "")}
                isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
                noOptionsText="No books found"
                clearOnEscape
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Book"
                    placeholder="Search books..."
                    sx={{ ...textFieldSx, "& .MuiInputLabel-root": inputLabelSx }}
                  />
                )}
                sx={{
                  "& .MuiAutocomplete-inputRoot": { color: textColor, backgroundColor: inputBackground },
                  "& .MuiOutlinedInput-notchedOutline": { borderColor },
                  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: theme.palette.primary.main },
                  "& .MuiSvgIcon-root": { color: textColor },
                }}
              />

              {/* TRANSACTION STATUS */}

              <FormControl
                fullWidth
                size="small"
                sx={{
                  "& .MuiInputLabel-root": inputLabelSx,
                }}
              >
                <InputLabel>Transaction Status</InputLabel>

                <Select
                  label="Transaction Status"
                  value={filters.transaction_status || ""}
                  onChange={(e) =>
                    handleFilterChange(
                      "transaction_status",
                      e.target.value
                    )
                  }
                  sx={selectSx}
                  MenuProps={menuProps}
                >
                  <MenuItem value="">All</MenuItem>

                  <MenuItem value="issued">Issued</MenuItem>

                  <MenuItem value="returned">Returned</MenuItem>

                  <MenuItem value="lost">Lost</MenuItem>

                  <MenuItem value="cancelled">Cancelled</MenuItem>
                </Select>
              </FormControl>

              {/* FINE STATUS */}

              <FormControl
                fullWidth
                size="small"
                sx={{
                  "& .MuiInputLabel-root": inputLabelSx,
                }}
              >
                <InputLabel>Fine Status</InputLabel>

                <Select
                  label="Fine Status"
                  value={filters.fine_status || ""}
                  onChange={(e) =>
                    handleFilterChange(
                      "fine_status",
                      e.target.value
                    )
                  }
                  sx={selectSx}
                  MenuProps={menuProps}
                >
                  <MenuItem value="">All</MenuItem>

                  <MenuItem value="pending">Pending</MenuItem>

                  <MenuItem value="paid">Paid</MenuItem>

                  <MenuItem value="waived">Waived</MenuItem>
                </Select>
              </FormControl>

              {/* CONDITION */}

              <FormControl
                fullWidth
                size="small"
                sx={{
                  "& .MuiInputLabel-root": inputLabelSx,
                }}
              >
                <InputLabel>Book Condition</InputLabel>

                <Select
                  label="Book Condition"
                  value={filters.book_condition || ""}
                  onChange={(e) =>
                    handleFilterChange(
                      "book_condition",
                      e.target.value
                    )
                  }
                  sx={selectSx}
                  MenuProps={menuProps}
                >
                  <MenuItem value="">All</MenuItem>

                  <MenuItem value="good">Good</MenuItem>

                  <MenuItem value="damaged">Damaged</MenuItem>

                  <MenuItem value="lost">Lost</MenuItem>
                </Select>
              </FormControl>

              {/* ACTIVE */}

              <FormControl
                fullWidth
                size="small"
                sx={{
                  "& .MuiInputLabel-root": inputLabelSx,
                }}
              >
                <InputLabel>Status</InputLabel>

                <Select
                  label="Status"
                  value={filters.is_active || ""}
                  onChange={(e) =>
                    handleFilterChange(
                      "is_active",
                      e.target.value
                    )
                  }
                  sx={selectSx}
                  MenuProps={menuProps}
                >
                  <MenuItem value="">All</MenuItem>

                  <MenuItem value="true">Active</MenuItem>

                  <MenuItem value="false">Inactive</MenuItem>
                </Select>
              </FormControl>

              {/* ISSUE DATE FROM */}

              <TextField
                fullWidth
                size="small"
                type="date"
                label="Issue Date From"
                InputLabelProps={{ shrink: true }}
                value={filters.issue_date_from || ""}
                onChange={(e) =>
                  handleFilterChange(
                    "issue_date_from",
                    e.target.value
                  )
                }
                sx={textFieldSx}
              />

              {/* ISSUE DATE TO */}

              <TextField
                fullWidth
                size="small"
                type="date"
                label="Issue Date To"
                InputLabelProps={{ shrink: true }}
                value={filters.issue_date_to || ""}
                onChange={(e) =>
                  handleFilterChange(
                    "issue_date_to",
                    e.target.value
                  )
                }
                sx={textFieldSx}
              />

              {/* DUE DATE FROM */}

              <TextField
                fullWidth
                size="small"
                type="date"
                label="Due Date From"
                InputLabelProps={{ shrink: true }}
                value={filters.due_date_from || ""}
                onChange={(e) =>
                  handleFilterChange(
                    "due_date_from",
                    e.target.value
                  )
                }
                sx={textFieldSx}
              />

              {/* DUE DATE TO */}

              <TextField
                fullWidth
                size="small"
                type="date"
                label="Due Date To"
                InputLabelProps={{ shrink: true }}
                value={filters.due_date_to || ""}
                onChange={(e) =>
                  handleFilterChange(
                    "due_date_to",
                    e.target.value
                  )
                }
                sx={textFieldSx}
              />
            </Box>
          </CustomScrollbar>

          {/* FILTER FOOTER (fixed) */}

          <Box
            sx={{
              flexShrink: 0,
              display: "flex",
              justifyContent: "flex-end",
              gap: 1,
              px: 3,
              py: 2,
              borderTop: `1px solid ${borderColor}`,
              backgroundColor: theme.palette.background.paper,
            }}
          >
            <Button
              variant="contained"
              onClick={() => setOpenFilterDialog(false)}
              disabled={loading}
              sx={{ color: textColor, borderColor }}
            >
              CANCEL
            </Button>

            <Button
              variant="contained"
              color="error"
              onClick={handleReset}
              disabled={loading}
            >
              RESET
            </Button>

            <Button
              variant="contained"
              onClick={handleApplyFilters}
              disabled={loading}
            >
              {loading ? (
                <CircularProgress size={22} color="inherit" />
              ) : (
                "APPLY"
              )}
            </Button>
          </Box>
        </Box>
      </Modal>

      {/* =====================================================
          TABLE
      ===================================================== */}

      <Paper
        elevation={3}
        sx={{
          borderRadius: "16px",
          overflow: "hidden",

          backgroundColor: theme.palette.background.paper,
          color: textColor,

          border: `1px solid ${
            isDark
              ? "rgba(255,255,255,0.08)"
              : "rgba(0,0,0,0.08)"
          }`,
        }}
      >
        <Divider />

        <TableContainer
          component={CustomScrollbar}
          sx={{ overflowX: "auto" }}
        >
          <Table
            sx={{
              minWidth: 1100,

              "& .MuiTableCell-root": {
                color: `${textColor} !important`,
                borderColor: theme.palette.divider,
              },

              "& .MuiTableBody-root .MuiTableRow-root": {
                backgroundColor:
                  theme.palette.background.paper,
              },

              "& .MuiTableBody-root .MuiTableRow-root:hover": {
                backgroundColor: tableRowHover,
              },

              "& .MuiTableHead-root .MuiTableCell-root": {
                fontWeight: 700,
                backgroundColor:
                  theme.palette.primary.main,
                color: `${theme.palette.primary.contrastText} !important`,
                whiteSpace: "nowrap",
              },
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell>S.No</TableCell>
                <TableCell>Transaction Code</TableCell>
                <TableCell>Member</TableCell>
                <TableCell>Book</TableCell>
                <TableCell>Issue Date</TableCell>
                <TableCell>Due Date</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Fine</TableCell>
                <TableCell>Condition</TableCell>
                <TableCell>Active</TableCell>
                <TableCell align="center">Action</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={11} align="center" sx={{ py: 5 }}>
                    <CircularProgress size={28} />
                  </TableCell>
                </TableRow>
              ) : rows.length > 0 ? (
                rows.map((row, index) => (
                  <TableRow key={row.id} hover>
                    <TableCell>
                      {page * rowsPerPage + index + 1}
                    </TableCell>

                    <TableCell>
                      <Typography
                        sx={{
                          color: `${textColor} !important`,
                          fontWeight: 600,
                        }}
                      >
                        {row.transaction_code || "-"}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Typography
                        component="div"
                        sx={{
                          color: `${textColor} !important`,
                          fontWeight: 600,
                        }}
                      >
                        {row.member_name || "-"}
                      </Typography>

                      <Typography
                        component="div"
                        variant="caption"
                        sx={{
                          color: `${secondaryTextColor} !important`,
                          display: "block",
                          mt: 0.3,
                        }}
                      >
                        {row.member_id || "-"}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Typography
                        component="div"
                        sx={{
                          color: `${textColor} !important`,
                          fontWeight: 600,
                        }}
                      >
                        {row.book_title || "-"}
                      </Typography>

                      <Typography
                        component="div"
                        variant="caption"
                        sx={{
                          color: `${secondaryTextColor} !important`,
                          display: "block",
                          mt: 0.3,
                        }}
                      >
                        {row.book_code || "-"}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      {row.issue_date || "-"}
                    </TableCell>

                    <TableCell>
                      {row.due_date || "-"}
                    </TableCell>

                    <TableCell>
                      {statusChip(
                        row.transaction_status,
                        row.transaction_status_display
                      )}
                    </TableCell>

                    <TableCell>
                      {row.fine_amount ?? "0"}
                    </TableCell>

                    <TableCell>
                      {conditionChip(
                        row.book_condition,
                        row.book_condition_display
                      )}
                    </TableCell>

                    <TableCell>
                      <Chip
                        size="small"
                        icon={
                          row.is_active ? (
                            <CheckCircle />
                          ) : (
                            <Cancel />
                          )
                        }
                        label={
                          row.is_active ? "Active" : "Inactive"
                        }
                        sx={{
                          fontWeight: 600,
                          borderRadius: "20px",

                          backgroundColor: row.is_active
                            ? "rgba(46,125,50,0.12)"
                            : "rgba(211,47,47,0.12)",

                          color: row.is_active
                            ? theme.palette.success.main
                            : theme.palette.error.main,

                          "& .MuiChip-icon": {
                            color: row.is_active
                              ? theme.palette.success.main
                              : theme.palette.error.main,
                          },

                          border: "none",
                        }}
                      />
                    </TableCell>

                    <TableCell align="center">
                      <Stack
                        direction="row"
                        justifyContent="center"
                        spacing={0.5}
                      >
                        {/* VIEW */}

                        <Tooltip title="View">
                          <IconButton
                            color="info"
                            onClick={() =>
                              fetchDetail(row.id, "view")
                            }
                          >
                            <Visibility />
                          </IconButton>
                        </Tooltip>

                        {/* EDIT */}

                        <Tooltip title="Edit">
                          <IconButton
                            color="primary"
                            onClick={() =>
                              fetchDetail(row.id, "edit")
                            }
                          >
                            <Edit />
                          </IconButton>
                        </Tooltip>

                        {/* ACTIVATE / DEACTIVATE */}

                        <Tooltip
                          title={
                            row.is_active
                              ? "Deactivate"
                              : "Activate"
                          }
                        >
                          <IconButton
                            color={
                              row.is_active
                                ? "warning"
                                : "success"
                            }
                            onClick={() =>
                              openStatusConfirmation(row)
                            }
                          >
                            {row.is_active ? (
                              <Cancel />
                            ) : (
                              <CheckCircle />
                            )}
                          </IconButton>
                        </Tooltip>

                        {/* DELETE */}

                        <Tooltip title="Delete">
                          <IconButton
                            color="error"
                            onClick={() =>
                              openDeleteConfirmation(row.id)
                            }
                          >
                            <Delete />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={11}
                    align="center"
                    sx={{
                      py: 5,
                      color: `${secondaryTextColor} !important`,
                    }}
                  >
                    No book issue transactions found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={(_event, newPage) =>
            setPage(newPage)
          }
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(
              parseInt(event.target.value, 10)
            );

            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50, 100]}
          sx={{
            color: textColor,

            "& .MuiTablePagination-selectLabel": {
              color: textColor,
            },

            "& .MuiTablePagination-displayedRows": {
              color: textColor,
            },

            "& .MuiSelect-select": {
              color: textColor,
            },

            "& .MuiSvgIcon-root": {
              color: textColor,
            },
          }}
        />
      </Paper>

      {/* =====================================================
          ADD / VIEW / EDIT MODAL
      ===================================================== */}

      <Modal
        open={openDialog}
        onClose={() => !formLoading && setOpenDialog(false)}
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "95%", sm: 600, md: 850 },
            maxWidth: "95%",
            maxHeight: "90vh",
            display: "flex",
            flexDirection: "column",
            bgcolor: theme.palette.background.paper,
            color: modalTextColor,
            borderRadius: 3,
            boxShadow: 24,
            overflow: "hidden",
            outline: "none",
          }}
        >
          {/* HEADER */}

          <Box
            sx={{
              flexShrink: 0,
              backgroundColor: theme.palette.primary.main,
              px: 2,
              py: 1.5,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Box>
              <Typography
                variant="h6"
                sx={{
                  color: "#ffffff",
                  fontWeight: 700,
                }}
              >
                {dialogMode === "create"
                  ? "Issue Book"
                  : dialogMode === "edit"
                  ? "Edit Book Issue Transaction"
                  : "Book Issue Transaction Details"}
              </Typography>

              {selectedId && dialogMode !== "create" && (
                <Typography
                  variant="caption"
                  sx={{
                    color: "rgba(255,255,255,0.85)",
                  }}
                >
                  Transaction ID: {selectedId}
                </Typography>
              )}
            </Box>

            <IconButton
              onClick={() => !formLoading && setOpenDialog(false)}
              sx={{ color: "#ffffff" }}
            >
              <Close />
            </IconButton>
          </Box>

          {/* FORM BODY (scrollable) */}

          <CustomScrollbar
            style={{
              flex: 1,
              minHeight: 0,
              padding: "24px 24px 8px",
            }}
          >
            {formLoading ? (
              <Box
                minHeight={300}
                display="flex"
                justifyContent="center"
                alignItems="center"
              >
                <CircularProgress />
              </Box>
            ) : (
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    sm: "repeat(2, minmax(0, 1fr))",
                  },
                  gap: 2,
                }}
              >
                {/* SEARCHABLE LIBRARY MEMBER */}
                <Autocomplete
                  fullWidth
                  size="small"
                  options={members}
                  value={members.find((member) => String(member.id) === String(form.library_member)) || null}
                  disabled={dialogMode === "view"}
                  onChange={(_, option) =>
                    handleFormChange("library_member", option ? String(option.id) : "")
                  }
                  getOptionLabel={(option) => String(getMemberLabel(option) || "")}
                  isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
                  noOptionsText="No library members found"
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Library Member *"
                      placeholder="Search members..."
                      sx={{ ...textFieldSx, "& .MuiInputLabel-root": inputLabelSx }}
                    />
                  )}
                  sx={{
                    "& .MuiAutocomplete-inputRoot": { color: textColor, backgroundColor: inputBackground },
                    "& .MuiOutlinedInput-notchedOutline": { borderColor },
                    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: theme.palette.primary.main },
                    "& .MuiSvgIcon-root": { color: textColor },
                  }}
                />

                {/* SEARCHABLE BOOK */}
                <Autocomplete
                  fullWidth
                  size="small"
                  options={books}
                  value={books.find((book) => String(book.id) === String(form.book)) || null}
                  disabled={dialogMode === "view"}
                  onChange={(_, option) =>
                    handleFormChange("book", option ? String(option.id) : "")
                  }
                  getOptionLabel={(option) => String(getBookLabel(option) || "")}
                  isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
                  noOptionsText="No books found"
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Book *"
                      placeholder="Search books..."
                      sx={{ ...textFieldSx, "& .MuiInputLabel-root": inputLabelSx }}
                    />
                  )}
                  sx={{
                    "& .MuiAutocomplete-inputRoot": { color: textColor, backgroundColor: inputBackground },
                    "& .MuiOutlinedInput-notchedOutline": { borderColor },
                    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: theme.palette.primary.main },
                    "& .MuiSvgIcon-root": { color: textColor },
                  }}
                />

                {/* ISSUE DATE */}

                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  label="Issue Date *"
                  InputLabelProps={{ shrink: true }}
                  value={form.issue_date || ""}
                  disabled={dialogMode === "view"}
                  onChange={(e) =>
                    handleFormChange(
                      "issue_date",
                      e.target.value
                    )
                  }
                  sx={textFieldSx}
                />

                {/* DUE DATE */}

                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  label="Due Date *"
                  InputLabelProps={{ shrink: true }}
                  value={form.due_date || ""}
                  disabled={dialogMode === "view"}
                  onChange={(e) =>
                    handleFormChange("due_date", e.target.value)
                  }
                  sx={textFieldSx}
                />

                {/* EXTRA FIELDS (edit only) */}

                {dialogMode !== "create" && (
                  <>
                    {form.transaction_status === "returned" && (
                      <TextField
                        fullWidth
                        size="small"
                        type="date"
                        label="Return Date *"
                        InputLabelProps={{ shrink: true }}
                        value={form.return_date || ""}
                        disabled={dialogMode === "view"}
                        required
                        onChange={(e) =>
                          handleFormChange(
                            "return_date",
                            e.target.value
                          )
                        }
                        sx={textFieldSx}
                      />
                    )}

                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      label="Overdue Days"
                      value={form.overdue_days ?? 0}
                      disabled={dialogMode === "view"}
                      inputProps={{ min: 0 }}
                      onChange={(e) =>
                        handleFormChange(
                          "overdue_days",
                          e.target.value
                        )
                      }
                      sx={textFieldSx}
                    />

                    <TextField
                      fullWidth
                      size="small"
                      label="Fine Amount"
                      value={form.fine_amount || "0"}
                      disabled={dialogMode === "view"}
                      inputProps={{ inputMode: "decimal" }}
                      onChange={(e) =>
                        handleFormChange(
                          "fine_amount",
                          decimalOnly(e.target.value)
                        )
                      }
                      sx={textFieldSx}
                    />

                    {/* FINE STATUS */}

                    <FormControl
                      fullWidth
                      size="small"
                      sx={{
                        "& .MuiInputLabel-root": inputLabelSx,
                      }}
                    >
                      <InputLabel>Fine Status</InputLabel>

                      <Select
                        label="Fine Status"
                        value={form.fine_status || "pending"}
                        disabled={dialogMode === "view"}
                        onChange={(e) =>
                          handleFormChange(
                            "fine_status",
                            e.target.value
                          )
                        }
                        sx={selectSx}
                        MenuProps={menuProps}
                      >
                        <MenuItem value="pending">Pending</MenuItem>

                        <MenuItem value="paid">Paid</MenuItem>

                        <MenuItem value="waived">Waived</MenuItem>
                      </Select>
                    </FormControl>

                    {/* CONDITION */}

                    <FormControl
                      fullWidth
                      size="small"
                      sx={{
                        "& .MuiInputLabel-root": inputLabelSx,
                      }}
                    >
                      <InputLabel>Book Condition</InputLabel>

                      <Select
                        label="Book Condition"
                        value={form.book_condition || "good"}
                        disabled={dialogMode === "view"}
                        onChange={(e) =>
                          handleFormChange(
                            "book_condition",
                            e.target.value
                          )
                        }
                        sx={selectSx}
                        MenuProps={menuProps}
                      >
                        <MenuItem value="good">Good</MenuItem>

                        <MenuItem value="damaged">Damaged</MenuItem>

                        <MenuItem value="lost">Lost</MenuItem>
                      </Select>
                    </FormControl>

                    {/* TRANSACTION STATUS */}

                    <FormControl
                      fullWidth
                      size="small"
                      sx={{
                        "& .MuiInputLabel-root": inputLabelSx,
                      }}
                    >
                      <InputLabel>Transaction Status</InputLabel>

                      <Select
                        label="Transaction Status"
                        value={form.transaction_status || "issued"}
                        disabled={dialogMode === "view"}
                        onChange={(e) =>
                          handleFormChange(
                            "transaction_status",
                            e.target.value
                          )
                        }
                        sx={selectSx}
                        MenuProps={menuProps}
                      >
                        <MenuItem value="issued">Issued</MenuItem>

                        <MenuItem value="returned">Returned</MenuItem>

                        <MenuItem value="lost">Lost</MenuItem>

                        <MenuItem value="cancelled">Cancelled</MenuItem>
                      </Select>
                    </FormControl>

                    {/* ACTIVE STATUS */}

                    <FormControl
                      fullWidth
                      size="small"
                      sx={{
                        "& .MuiInputLabel-root": inputLabelSx,
                      }}
                    >
                      <InputLabel>Active Status</InputLabel>

                      <Select
                        label="Active Status"
                        value={form.is_active ? "true" : "false"}
                        disabled={dialogMode === "view"}
                        onChange={(e) =>
                          handleFormChange(
                            "is_active",
                            e.target.value === "true"
                          )
                        }
                        sx={selectSx}
                        MenuProps={menuProps}
                      >
                        <MenuItem value="true">Active</MenuItem>

                        <MenuItem value="false">Inactive</MenuItem>
                      </Select>
                    </FormControl>
                  </>
                )}

                {/* REMARKS */}

                <TextField
                  fullWidth
                  multiline
                  minRows={3}
                  label="Remarks"
                  value={form.remarks || ""}
                  disabled={dialogMode === "view"}
                  onChange={(e) =>
                    handleFormChange("remarks", e.target.value)
                  }
                  sx={{
                    ...textFieldSx,
                    gridColumn: { sm: "1 / -1" },
                  }}
                />
              </Box>
            )}
          </CustomScrollbar>

          {/* FOOTER (fixed) */}

          <Box
            sx={{
              flexShrink: 0,
              display: "flex",
              justifyContent: "flex-end",
              gap: 1,
              px: 3,
              py: 2,
              borderTop: `1px solid ${borderColor}`,
              backgroundColor: theme.palette.background.paper,
            }}
          >
            {dialogMode === "view" && (
              <Button
                variant="contained"
                startIcon={<Print />}
                onClick={handlePrintPdf}
                disabled={formLoading}
                sx={{ fontWeight: 700 }}
              >
                PRINT PDF
              </Button>
            )}

            <Button
              variant="contained"
              onClick={() => setOpenDialog(false)}
              disabled={formLoading}
              sx={{
                color: textColor,
                borderColor,

                "&:hover": {
                  borderColor: theme.palette.primary.main,
                },
              }}
            >
              CLOSE
            </Button>

            {dialogMode === "create" && (
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={
                  formLoading ||
                  !selectedBranchId ||
                  !form.library_member ||
                  !form.book ||
                  !form.issue_date ||
                  !form.due_date
                }
              >
                {formLoading ? (
                  <CircularProgress size={22} color="inherit" />
                ) : (
                  "ISSUE BOOK"
                )}
              </Button>
            )}

            {dialogMode === "edit" && (
              <Button
                variant="contained"
                onClick={handleUpdate}
                disabled={formLoading}
              >
                {formLoading ? (
                  <CircularProgress size={22} color="inherit" />
                ) : (
                  "UPDATE"
                )}
              </Button>
            )}
          </Box>
        </Box>
      </Modal>

      {/* =====================================================
          BULK ISSUE MODAL
      ===================================================== */}

      <Modal
        open={openBulkDialog}
        onClose={() => !formLoading && setOpenBulkDialog(false)}
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 900,
            maxWidth: "95%",
            maxHeight: "90vh",
            display: "flex",
            flexDirection: "column",
            bgcolor: theme.palette.background.paper,
            color: modalTextColor,
            borderRadius: 3,
            boxShadow: 24,
            overflow: "hidden",
            outline: "none",
          }}
        >
          {/* HEADER (fixed) */}

          <Box
            sx={{
              flexShrink: 0,
              backgroundColor: theme.palette.primary.main,
              px: 2,
              py: 1.5,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography
              variant="h6"
              sx={{ color: "#ffffff", fontWeight: 700 }}
            >
              Bulk Issue Books
            </Typography>

            <IconButton
              onClick={() =>
                !formLoading && setOpenBulkDialog(false)
              }
              sx={{ color: "#ffffff" }}
            >
              <Close />
            </IconButton>
          </Box>

          {/* BODY (scrollable) */}

          <CustomScrollbar
            style={{
              flex: 1,
              minHeight: 0,
              padding: "24px",
            }}
          >
            <Typography
              variant="body2"
              sx={{ mb: 2, color: secondaryTextColor }}
            >
              Duplicate Member + Book combinations inside one
              request are not allowed. Books already issued to
              the member are skipped, and books without available
              copies are skipped automatically.
            </Typography>

            {bulkRows.map((row, index) => (
              <Box
                key={index}
                sx={{
                  border: `1px solid ${borderColor}`,
                  borderRadius: 2,
                  p: 2,
                  mb: 2,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 2,
                  }}
                >
                  <Typography sx={{ fontWeight: 600 }}>
                    Transaction {index + 1}
                  </Typography>

                  <IconButton
                    color="error"
                    size="small"
                    onClick={() => removeBulkRow(index)}
                    disabled={bulkRows.length === 1}
                  >
                    <Delete />
                  </IconButton>
                </Box>

                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "1fr",
                      sm: "repeat(2, minmax(0, 1fr))",
                    },
                    gap: 2,
                  }}
                >
                  {/* SEARCHABLE LIBRARY MEMBER */}
                  <Autocomplete
                    fullWidth
                    size="small"
                    options={members}
                    value={members.find((member) => String(member.id) === String(row.library_member)) || null}
                    onChange={(_, option) =>
                      updateBulkRow(index, "library_member", option ? String(option.id) : "")
                    }
                    getOptionLabel={(option) => String(getMemberLabel(option) || "")}
                    isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
                    noOptionsText="No library members found"
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Library Member *"
                        placeholder="Search members..."
                        sx={{ ...textFieldSx, "& .MuiInputLabel-root": inputLabelSx }}
                      />
                    )}
                    sx={{
                      "& .MuiAutocomplete-inputRoot": { color: textColor, backgroundColor: inputBackground },
                      "& .MuiOutlinedInput-notchedOutline": { borderColor },
                      "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: theme.palette.primary.main },
                      "& .MuiSvgIcon-root": { color: textColor },
                    }}
                  />

                  {/* SEARCHABLE BOOK */}
                  <Autocomplete
                    fullWidth
                    size="small"
                    options={books}
                    value={books.find((book) => String(book.id) === String(row.book)) || null}
                    onChange={(_, option) =>
                      updateBulkRow(index, "book", option ? String(option.id) : "")
                    }
                    getOptionLabel={(option) => String(getBookLabel(option) || "")}
                    isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
                    noOptionsText="No books found"
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Book *"
                        placeholder="Search books..."
                        sx={{ ...textFieldSx, "& .MuiInputLabel-root": inputLabelSx }}
                      />
                    )}
                    sx={{
                      "& .MuiAutocomplete-inputRoot": { color: textColor, backgroundColor: inputBackground },
                      "& .MuiOutlinedInput-notchedOutline": { borderColor },
                      "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: theme.palette.primary.main },
                      "& .MuiSvgIcon-root": { color: textColor },
                    }}
                  />

                  {/* ISSUE DATE */}

                  <TextField
                    fullWidth
                    size="small"
                    type="date"
                    label="Issue Date *"
                    InputLabelProps={{ shrink: true }}
                    value={row.issue_date}
                    onChange={(e) => {
                      const value = e.target.value;

                      updateBulkRow(index, "issue_date", value);
                      updateBulkRow(
                        index,
                        "due_date",
                        getDatePlusDays(value, 7)
                      );
                    }}
                    sx={textFieldSx}
                  />

                  {/* DUE DATE */}

                  <TextField
                    fullWidth
                    size="small"
                    type="date"
                    label="Due Date *"
                    InputLabelProps={{ shrink: true }}
                    value={row.due_date}
                    onChange={(e) =>
                      updateBulkRow(
                        index,
                        "due_date",
                        e.target.value
                      )
                    }
                    sx={textFieldSx}
                  />

                  {/* REMARKS */}

                  <TextField
                    fullWidth
                    size="small"
                    label="Remarks"
                    value={row.remarks}
                    onChange={(e) =>
                      updateBulkRow(
                        index,
                        "remarks",
                        e.target.value
                      )
                    }
                    sx={{
                      ...textFieldSx,
                      gridColumn: { sm: "1 / -1" },
                    }}
                  />
                </Box>
              </Box>
            ))}

            <Button
              startIcon={<Add />}
              onClick={addBulkRow}
              sx={{ color: textColor }}
            >
              Add another transaction
            </Button>
          </CustomScrollbar>

          {/* FOOTER (fixed) */}

          <Box
            sx={{
              flexShrink: 0,
              display: "flex",
              justifyContent: "flex-end",
              gap: 1,
              px: 3,
              py: 2,
              borderTop: `1px solid ${borderColor}`,
              backgroundColor: theme.palette.background.paper,
            }}
          >
            <Button
              variant="contained"
              onClick={handleBulkSubmit}
              disabled={formLoading || !selectedBranchId}
            >
              {formLoading ? (
                <CircularProgress size={22} color="inherit" />
              ) : (
                "ISSUE ALL"
              )}
            </Button>

            <Button
              variant="contained"
              onClick={() => setOpenBulkDialog(false)}
              disabled={formLoading}
              sx={{ color: textColor, borderColor }}
            >
              CANCEL
            </Button>
          </Box>
        </Box>
      </Modal>

      {/* =====================================================
          DELETE / STATUS CONFIRMATION MODAL
      ===================================================== */}

      <Modal
        open={confirmModal.open}
        onClose={closeConfirmation}
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",

            width: {
              xs: "90%",
              sm: 450,
            },

            bgcolor: theme.palette.background.paper,
            color: modalTextColor,
            borderRadius: 3,
            boxShadow: 24,
            outline: "none",
            overflow: "hidden",
          }}
        >
          {/* CONFIRM HEADER */}

          <Box
            sx={{
              px: 3,
              py: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              borderBottom: `1px solid ${borderColor}`,
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: textColor,
              }}
            >
              {confirmModal.type === "delete"
                ? "Delete Transaction"
                : confirmModal.row?.is_active
                ? "Deactivate Transaction"
                : "Activate Transaction"}
            </Typography>

            <IconButton
              onClick={closeConfirmation}
              disabled={confirmLoading}
              sx={{ color: textColor }}
            >
              <Close />
            </IconButton>
          </Box>

          {/* CONFIRM BODY */}

          <Box sx={{ p: 3 }}>
            <Box
              display="flex"
              justifyContent="center"
              mb={2}
            >
              <Warning
                sx={{
                  fontSize: 48,
                  color:
                    confirmModal.type === "delete"
                      ? theme.palette.error.main
                      : confirmModal.row?.is_active
                      ? theme.palette.warning.main
                      : theme.palette.success.main,
                }}
              />
            </Box>

            <Typography
              align="center"
              sx={{
                color: textColor,
                fontWeight: 600,
                mb: 1,
              }}
            >
              {confirmModal.type === "delete"
                ? "Are you sure you want to delete this transaction?"
                : confirmModal.row?.is_active
                ? "Are you sure you want to deactivate this transaction?"
                : "Are you sure you want to activate this transaction?"}
            </Typography>

            <Typography
              align="center"
              variant="body2"
              sx={{ color: secondaryTextColor, mb: 3 }}
            >
              {confirmModal.type === "delete"
                ? "This is a soft delete. The record will not be permanently removed."
                : confirmModal.row?.is_active
                ? "The transaction will become inactive."
                : "The transaction will become active again."}
            </Typography>

            {/* BUTTONS */}

            <Stack
              direction="row"
              spacing={1.5}
              justifyContent="flex-end"
            >
              <Button
                variant="contained"
                onClick={closeConfirmation}
                disabled={confirmLoading}
                sx={{
                  color: textColor,
                  borderColor,
                }}
              >
                CANCEL
              </Button>

              <Button
                variant="contained"
                color={
                  confirmModal.type === "delete"
                    ? "error"
                    : confirmModal.row?.is_active
                    ? "warning"
                    : "success"
                }
                onClick={handleConfirmAction}
                disabled={confirmLoading}
              >
                {confirmLoading ? (
                  <CircularProgress size={22} color="inherit" />
                ) : confirmModal.type === "delete" ? (
                  "DELETE"
                ) : confirmModal.row?.is_active ? (
                  "DEACTIVATE"
                ) : (
                  "ACTIVATE"
                )}
              </Button>
            </Stack>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
}